import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { Connection } from '@3d-scene/model3d/connection';
import { ArticleUtils } from '@article/util/article-utils';
import { TextureAreaVO } from '@shared/vo/meta-element.vo';
import { LabelEditorStateManager } from './label-editor-state.manager';
import { LabelEditorMode } from '../label-editor.component';
import { UserUtils } from '@user/service/user-utils';
import { TextureConnectionUtils } from '@3d-scene/processor/texture-connection-utils';
import { ProjectModel } from '@project/model/project.model';
import { MountingRailHelper } from '@project/helper/mounting-rail.helper';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ProjectHelper } from '@project/helper/project.helper';
import { SelectionArticleGroupIdentity } from '../../../article/model/vo/selection-article-group-identity';
import { ArticleTypeIds } from '@article/constants/article-type-ids';
import { ArticleUsageVO } from '@article/model/vo/article.vo';
import { Injectable } from '@angular/core';
import { OpenLabelEditorService } from './open-label-editor.service';
import { SelectionModelService } from '../../../article/service/selection-model.service';
import { ProjectUtils } from '../../../project/util/project-utils';
import { IsProjectType } from '@project/model/is-project-type';
import { ArrayUtils } from '@shared/util/array-utils';
import { DeactivateProjectChangeActionService } from '@shared/service/deactivate-project-change-action.service';

@Injectable({ providedIn: 'root' })
export class OpenLabelEditorHelper {

  constructor(
    private selectionModelService: SelectionModelService,
    private labelEditorStateManager: LabelEditorStateManager,
    private projectModel: ProjectModel,
    private mountingRailHelper: MountingRailHelper,
    private projectHelper: ProjectHelper,
    private containerService: ContainerService,
    private textureConnectionUtils: TextureConnectionUtils,
    private openLabelEditorService: OpenLabelEditorService,
    private deactivateProjectChangeActionService: DeactivateProjectChangeActionService,
  ) {}

  public showLabelEditorForCoverLabelArticles(containers: ArticleContainer[]): void {
    if (!containers || containers.length === 0) {
      return;
    }
    const textureConnections: Connection[] = [];
    for (const articleContainer of containers) {
      if (!ArticleUtils.isCoverLabelStripArticle(articleContainer.article)) {
        continue;
      }
      const textureMetaElements: TextureAreaVO[] = ArticleUtils.getTextureElements(articleContainer.article);
      for (const textureArea of textureMetaElements) {
        const cube: Connection = articleContainer.findFirstConnectionByName(textureArea.name);
        if (cube) {
          textureConnections.push(cube);
        }
      }
    }
    if (textureConnections.length > 0) {
      if (containers.some((c) => !c.articleSelected)) {
        this.selectionModelService.removeAllSelections();
        this.selectionModelService.selectArticlesByUsageIds(containers.map((c) => c.articleUsage.id));
      }
      if (this.labelEditorStateManager.labelEditorMode === LabelEditorMode.CLOSED) {
        this.openLabelEditorService.openForData({
          connectionCubes: textureConnections,
          validateSizes: true,
          selectAllCellsOnInit: false,
        });
      }
    }
  }

  public async showLabelEditorForMiniTerminalBlocksProject(): Promise<void> {
    this.selectMiniTerminalBlocks();
    await this.openLabelEditorService.openForData({
      connectionCubes: this.getSelectedTextConnections(),
      validateSizes: true,
      selectAllCellsOnInit: true,
    });
  }

  private selectMiniTerminalBlocks(): void {
    this.selectionModelService.removeAllSelections();
    const mountingRailUsage: ArticleUsageVO = this.mountingRailHelper.getMountingRailUsage();
    const articleContainers: ArticleContainer[] = this.containerService.getConnectedArticleContainers(mountingRailUsage);
    const excludedArticleTypeIds: number[] = [
      ArticleTypeIds.DRILLING_HOLE,
      ArticleTypeIds.BLOCKING_ZONE,
      ArticleTypeIds.END_PLATE,
      ArticleTypeIds.END_TERMINAL,
    ];
    const articleContainersToSelect: ArticleContainer[] = articleContainers.filter((articleContainer: ArticleContainer) => {
      return !ArticleUtils.isArticleOfAnyTypeId(articleContainer.article, excludedArticleTypeIds);
    });
    for (const articleContainer of articleContainersToSelect) {
      const articleSelectionIdentity: SelectionArticleGroupIdentity = this.projectHelper.createSelection(articleContainer.articleUsage);
      this.selectionModelService.selectObject(articleSelectionIdentity, true, false);
    }
  }

  public getSelectedTextConnections(): Connection[] {
    const onlyDefaultLabelingPermission: boolean = !UserUtils.hasPermissionToEditAllLabelings(this.projectModel.user);
    return this.textureConnectionUtils.getSelectedTextConnectionsWithRelative(onlyDefaultLabelingPermission);
  }

  public isOpenLabelEditorAvailable(): boolean {
    if (this.deactivateProjectChangeActionService.shouldDeactivateProjectChangeAction() ||
      !ProjectUtils.isProjectEditable(this.projectModel.currentProject, this.projectModel.user)
    ) {
      return false;
    }
    if (this.labelEditorStateManager.labelEditorMode === LabelEditorMode.TEXT_CHECK_FAILED) {
      return false;
    }
    if (
      IsProjectType.mounting(this.projectModel.currentProjectType) ||
      IsProjectType.markingSystem(this.projectModel.currentProjectType) ||
      IsProjectType.splicingConnector221(this.projectModel.currentProjectType) ||
      IsProjectType.ltl(this.projectModel.currentProjectType)
    ) {
      return ArrayUtils.isNotEmpty(this.getSelectedTextConnections()) || ArrayUtils.isNotEmpty(this.getSelectedUnusedLabelingConnections());
    }
    return false;
  }

  private getSelectedUnusedLabelingConnections(): Connection[] {
    return this.textureConnectionUtils.getSelectedUnusedLabelingConnectionsWithRelative();
  }
}
